import { useEffect, useMemo } from 'react';

import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useFacilitiesTableData } from '~/core/routes/credit-facilities/hooks/useFacilitiesTableData';
import { useFacilitiesTableDataSls } from '~/core/routes/credit-facilities/hooks/useFacilitiesTableDataSls';
import { UsePortfolioFacilitiesStore } from '~/core/routes/credit-facilities/PortfolioFacilitiesProvider';
import {
  FacilitiesFilterState,
  StaticFiltersState,
  StringFilter,
} from '~/core/routes/credit-facilities/PortfolioFacilitiesProvider/facilitiesFilterTypes';

const INITIAL_STANDARD_FILTERS: Pick<
  StaticFiltersState,
  | 'maturityDate'
  | 'closingDate'
  | 'dateOfLastFundingRequest'
  | 'globalCommitmentAmount'
  | 'globalLoanOutstanding'
  | 'globalUnusedCommitment'
  | 'globalUtilization'
  | 'collectionsAccountBalance'
  | 'requiredReserve'
  | 'totalBorrowingBase'
  | 'borrowingBaseFromReceivables'
  | 'accruedInterestAndFees'
  | 'borrowingBaseAvailability'
> = {
  maturityDate: { startDate: null, endDate: null },
  closingDate: { startDate: null, endDate: null },
  dateOfLastFundingRequest: { startDate: null, endDate: null },
  globalCommitmentAmount: { min: null, max: null },
  globalLoanOutstanding: { min: null, max: null },
  globalUnusedCommitment: { min: null, max: null },
  globalUtilization: { min: null, max: null },
  collectionsAccountBalance: { min: null, max: null },
  requiredReserve: { min: null, max: null },
  totalBorrowingBase: { min: null, max: null },
  borrowingBaseFromReceivables: { min: null, max: null },
  accruedInterestAndFees: { min: null, max: null },
  borrowingBaseAvailability: { min: null, max: null },
};

const INITIAL_CAPITAL_PROVIDER_FILTERS: Pick<
  StaticFiltersState,
  | 'capitalProviderCommitmentAmount'
  | 'capitalProviderLoanOutstanding'
  | 'capitalProviderUnused'
  | 'capitalProviderUtilization'
> = {
  capitalProviderCommitmentAmount: { min: null, max: null },
  capitalProviderLoanOutstanding: { min: null, max: null },
  capitalProviderUnused: { min: null, max: null },
  capitalProviderUtilization: { min: null, max: null },
};

export const useFetchFacilitiesTableData = (
  useStore: UsePortfolioFacilitiesStore,
) => {
  const flags = useFeatureFlags();
  const creditFacilities = useCreditFacilitiesOrThrow();

  const setFilters = useStore((store) => store.setFilters);
  const setInitialFilters = useStore((store) => store.setInitialFilters);
  const setFacilitiesTableData = useStore(
    (store) => store.setFacilitiesTableData,
  );

  const facilitiesTableData = useFacilitiesTableData();
  const facilitiesTableDataSls = useFacilitiesTableDataSls();

  const tableData = flags.isSlsAgentLender
    ? facilitiesTableDataSls
    : facilitiesTableData;

  const facilityIds: StringFilter = useMemo(() => {
    return {
      selectedOptions: creditFacilities.facilities.map(
        (facility) => facility.id,
      ),
      options:
        creditFacilities.facilities.map((facility) => ({
          id: facility.id,
          name: facility.name,
        })) ?? [],
    };
  }, [creditFacilities]);

  const initialFacilitiesFilters: FacilitiesFilterState = useMemo(() => {
    const activeFilters = tableData.computedRows.length
      ? Object.keys(tableData.computedRows[0])
      : [];
    return {
      staticFilters: {
        facilityIds,
        ...Object.entries(INITIAL_STANDARD_FILTERS).reduce(
          (acc, [key, value]) => {
            if (activeFilters.includes(key)) {
              return { ...acc, [key]: value };
            }
            return acc;
          },
          {},
        ),
        ...Object.entries(INITIAL_CAPITAL_PROVIDER_FILTERS).reduce(
          (acc, [key, value]) => {
            if (activeFilters.includes(key)) {
              return { ...acc, [key]: value };
            }
            return acc;
          },
          {},
        ),
      },
      attributeFilters: {}, // todo @mark
    };
  }, [facilityIds, tableData.computedRows]);

  useEffect(() => {
    setFacilitiesTableData(tableData);
    setInitialFilters(initialFacilitiesFilters);
    setFilters(initialFacilitiesFilters);
  }, [
    tableData,
    initialFacilitiesFilters,
    setFacilitiesTableData,
    setFilters,
    setInitialFilters,
  ]);
};
